import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import SharedNavbar from '@/components/feature/SharedNavbar';
import SharedFooter from '@/components/feature/SharedFooter';
import { Seo } from '@/components/Seo';
import { supabase } from '@/lib/supabaseClient';
import RetailerMap from '@/components/whereToBuy/RetailerMap';
import { useSiteDesign } from '@/hooks/useSiteDesign';
import StoresV2 from './v2/StoresV2';

interface Retailer {
  id: string;
  name: string;
  type: string;
  area: string;
  description: string | null;
  address: string | null;
  hours: string | null;
  phone: string | null;
  map_link: string | null;
  latitude: number | null;
  longitude: number | null;
}

interface PartnerListingRow {
  id: string;
  name: string;
  type: string;
  district: string | null;
  contact: string | null;
  address: string | null;
  map_link: string | null;
  hours: string | null;
  description: string | null;
  latitude: number | null;
  longitude: number | null;
}

// core_partners.type -> the retailer "type" filter used on this page.
const PARTNER_TYPE_LABEL: Record<string, string> = {
  pharmacy: 'Pharmacy',
  reseller: 'Retail',
};

const areas = ['All', 'Gulshan', 'Banani', 'Dhanmondi', 'Uttara', 'Bashundhara', 'Others'];
const types = ['All', 'Pharmacy', 'Online', 'Retail'];

export default function WhereToBuyPage() {
  const { isNew } = useSiteDesign();
  return isNew ? <StoresV2 /> : <ClassicWhereToBuy />;
}

function ClassicWhereToBuy() {
  const [retailers, setRetailers] = useState<Retailer[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('All');
  const [filterArea, setFilterArea] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const { t } = useTranslation();
  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';

  useEffect(() => {
    Promise.all([
      supabase
        .from('rxk_retailers')
        .select('id, name, type, area, description, address, hours, phone, map_link, latitude, longitude')
        .eq('status', 'active')
        .order('sort_order', { ascending: true }),
      // Approved resellers/pharmacies opted into public listing (toggled in
      // ERP) show up here automatically — no separate manual entry needed.
      supabase
        .from('core_partners_public_v')
        .select('id, name, type, district, contact, address, map_link, hours, description, latitude, longitude'),
    ]).then(([retailerRes, partnerRes]) => {
      const manual = (retailerRes.data as Retailer[]) ?? [];
      const fromPartners: Retailer[] = ((partnerRes.data as PartnerListingRow[]) ?? []).map((p) => ({
        id: p.id,
        name: p.name,
        type: PARTNER_TYPE_LABEL[p.type] ?? 'Retail',
        area: p.district ?? 'Others',
        description: p.description,
        address: p.address,
        hours: p.hours,
        phone: p.contact,
        map_link: p.map_link,
        latitude: p.latitude,
        longitude: p.longitude,
      }));
      setRetailers([...manual, ...fromPartners]);
      setLoading(false);
    });
  }, []);

  const filtered = retailers.filter((r) => {
    const matchType = filterType === 'All' || r.type.toLowerCase() === filterType.toLowerCase();
    const matchArea = filterArea === 'All' || r.area === filterArea || (filterArea === 'Others' && !areas.slice(1, -1).includes(r.area));
    const matchSearch = !searchQuery || r.name.toLowerCase().includes(searchQuery.toLowerCase()) || r.area.toLowerCase().includes(searchQuery.toLowerCase());
    return matchType && matchArea && matchSearch;
  });

  return (
    <>
      <Seo pageKey="whereToBuy" />
      <SharedNavbar />
      <main className="w-full min-h-screen bg-white font-sans pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          {/* Hero */}
          <div className="flex flex-col items-center gap-4 mb-12 md:mb-16 text-center">
            <div className="flex items-center gap-3">
              <div className="w-8 h-px bg-[#7B1113]" />
              <span className="text-[11px] tracking-[0.2em] uppercase text-[#7B1113] font-medium">
                {t('whereToBuy.hero.label')}
              </span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-[#2A2A2A] leading-[1.1]">
              {t('whereToBuy.hero.heading')}
            </h1>
            <p className="text-sm text-[#8A8A8A] font-light max-w-lg">
              {t('whereToBuy.hero.subtitle')}
            </p>
          </div>

          {/* Official Online Store — Full width, prominent */}
          <div className="relative overflow-hidden rounded-2xl bg-[#7B1113] text-white mb-12 p-8 md:p-12">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 relative z-10">
              <div className="flex flex-col gap-2">
                <span className="text-[10px] tracking-[0.2em] uppercase text-white/50 font-medium">
                  {t('common.officialStore')}
                </span>
                <h2 className="font-serif text-2xl md:text-3xl font-bold">{t('whereToBuy.officialStore')}</h2>
                <p className="text-sm text-white/60 font-light">{t('whereToBuy.shopDirect')}</p>
              </div>
              <div className="flex items-center gap-4">
                <Link
                  to={prefix + '/products'}
                  className="px-8 py-3.5 bg-white text-[#7B1113] text-xs tracking-[0.15em] uppercase rounded-full hover:bg-white/90 transition-colors cursor-pointer text-center font-bold"
                >
                  {t('whereToBuy.shopNow')}
                </Link>
              </div>
            </div>
            {/* Decorative shopping bag */}
            <div className="absolute -right-4 -bottom-4 opacity-10 pointer-events-none">
              <img
                src="https://api.thekseoul.com/storage/v1/object/public/rxk-content/legacy/1168035b-9ebc-45be-b5a5-1e2388686df8_unnamed.png"
                alt=""
                className="w-40 h-40 object-contain"
              />
            </div>
          </div>

          {/* Search & Filter */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A8A] text-sm"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('whereToBuy.searchPlaceholder')}
                className="w-full pl-9 pr-4 py-3 rounded-xl bg-[#F0EFED] text-sm text-[#2A2A2A] placeholder:text-[#8A8A8A] focus:outline-none focus:ring-1 focus:ring-[#7B1113]"
              />
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="flex flex-col gap-2">
              <span className="text-[10px] tracking-wider uppercase text-[#8A8A8A] font-medium">{t('whereToBuy.filterArea')}</span>
              <div className="flex items-center gap-2 flex-wrap">
                {areas.map((a) => (
                  <button
                    key={a}
                    onClick={() => setFilterArea(a)}
                    className={`text-[10px] tracking-wider uppercase px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                      filterArea === a ? 'bg-[#2A2A2A] text-white' : 'bg-[#F0EFED] text-[#8A8A8A] hover:text-[#2A2A2A]'
                    }`}
                  >
                    {a}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <span className="text-[10px] tracking-wider uppercase text-[#8A8A8A] font-medium">{t('whereToBuy.filterType')}</span>
              <div className="flex items-center gap-2 flex-wrap">
                {types.map((ty) => (
                  <button
                    key={ty}
                    onClick={() => setFilterType(ty)}
                    className={`text-[10px] tracking-wider uppercase px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                      filterType === ty ? 'bg-[#2A2A2A] text-white' : 'bg-[#F0EFED] text-[#8A8A8A] hover:text-[#2A2A2A]'
                    }`}
                  >
                    {ty}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {!loading && (
            <p className="text-[11px] text-[#8A8A8A] font-light mb-6">
              {t('whereToBuy.showingResults', { count: filtered.length, total: retailers.length })}
            </p>
          )}

          {/* Retailer Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
            {filtered.map((retailer) => {
              const hasMap = !!retailer.map_link;
              const CardTag = hasMap ? 'a' : 'div';
              return (
                <CardTag
                  key={retailer.id}
                  {...(hasMap
                    ? { href: retailer.map_link!, rel: 'nofollow noopener noreferrer', target: '_blank' }
                    : {})}
                  className={`group relative flex flex-col gap-4 p-6 rounded-2xl bg-[#F0EFED] transition-all ${
                    hasMap ? 'cursor-pointer hover:bg-[#E7E5E1] hover:shadow-md hover:-translate-y-0.5' : ''
                  }`}
                  title={hasMap ? t('common.getDirections') : undefined}
                >
                  {hasMap && (
                    <div className="absolute top-5 right-5 w-7 h-7 rounded-full border border-[#2A2A2A]/15 flex items-center justify-center text-[#8A8A8A] group-hover:border-[#7B1113] group-hover:text-[#7B1113] transition-colors">
                      <i className="ri-arrow-right-up-line text-xs"></i>
                    </div>
                  )}

                  <div className="flex items-center gap-3 pr-8">
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                      <i className="ri-store-2-line text-[#1B3A5C] text-lg"></i>
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm font-medium text-[#2A2A2A] line-clamp-2">{retailer.name}</span>
                      <span className="text-[10px] text-[#8A8A8A] font-light uppercase tracking-wider">{retailer.type}</span>
                    </div>
                  </div>

                  {retailer.description && <p className="text-xs text-[#8A8A8A] font-light">{retailer.description}</p>}

                  <div className="flex flex-col gap-2 mt-auto pt-1 border-t border-[#2A2A2A]/[0.06]">
                    {retailer.address && (
                      <div className="flex items-start gap-2 pt-2">
                        <i className="ri-map-pin-line text-[#8A8A8A] text-xs mt-0.5 flex-shrink-0"></i>
                        <span className="text-xs text-[#8A8A8A] font-light">{retailer.address}</span>
                      </div>
                    )}
                    {(retailer.hours || retailer.phone) && (
                      <div className="flex items-center flex-wrap gap-x-4 gap-y-1.5">
                        {retailer.hours && (
                          <div className="flex items-center gap-2">
                            <i className="ri-time-line text-[#8A8A8A] text-xs flex-shrink-0"></i>
                            <span className="text-xs text-[#8A8A8A] font-light">{retailer.hours}</span>
                          </div>
                        )}
                        {retailer.phone && (
                          <div className="flex items-center gap-2">
                            <i className="ri-phone-line text-[#8A8A8A] text-xs flex-shrink-0"></i>
                            <span className="text-xs text-[#8A8A8A] font-light">{retailer.phone}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </CardTag>
              );
            })}
          </div>

          {/* Authorized Partners Coming Soon Banner */}
          <div className="w-full rounded-2xl border border-[#2A2A2A]/10 bg-[#FAFAF9] p-8 md:p-10 mb-16 flex flex-col items-center text-center">
            <div className="w-12 h-12 rounded-full bg-white border border-[#2A2A2A]/10 flex items-center justify-center mb-4">
              <i className="ri-store-2-line text-[#7B1113] text-xl"></i>
            </div>
            <h3 className="font-serif text-lg md:text-xl font-semibold text-[#2A2A2A] mb-3">
              {t('whereToBuy.authorizedPartners.heading')}
            </h3>
            <p className="text-sm text-[#8A8A8A] font-light leading-relaxed max-w-3xl mb-6">
              {t('whereToBuy.authorizedPartners.description')}
            </p>
            <Link
              to={prefix + '/partner-apply'}
              className="inline-block px-6 py-2.5 bg-[#7B1113] text-white text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors cursor-pointer text-center"
            >
              {t('whereToBuy.authorizedPartners.button')}
            </Link>
          </div>

          {/* Map Section */}
          <div className="mb-16">
            <h2 className="font-serif text-xl font-bold text-[#2A2A2A] mb-4">
              {t('whereToBuy.map.title')}
            </h2>
            <p className="text-sm text-[#8A8A8A] font-light mb-6">
              {t('whereToBuy.map.subtitle')}
            </p>

            <div className="w-full rounded-2xl overflow-hidden bg-[#F0EFED]">
              <RetailerMap retailers={filtered} />
            </div>
          </div>

          {/* Partner Apply CTA */}
          <div className="p-8 rounded-2xl bg-[#F0EFED] mb-12 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-col gap-1">
              <h3 className="font-serif text-lg font-semibold text-[#2A2A2A]">{t('whereToBuy.partnerApply')}</h3>
              <p className="text-xs text-[#8A8A8A] font-light">{t('whereToBuy.partnerApplyDesc')}</p>
            </div>
            <Link
              to={prefix + '/partner-apply'}
              className="px-6 py-2.5 bg-[#7B1113] text-white text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors cursor-pointer text-center"
            >
              {t('whereToBuy.applyNow')}
            </Link>
          </div>

          {/* Trust Section */}
          <div className="p-8 rounded-2xl bg-[#FAFAF9] flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex flex-col gap-2">
              <h3 className="font-serif text-xl font-semibold text-[#2A2A2A]">
                {t('whereToBuy.authenticityGuaranteed')}
              </h3>
              <p className="text-xs text-[#8A8A8A] font-light max-w-md">
                {t('whereToBuy.qrVerify')}
              </p>
              <Link
                to={prefix + '/verify'}
                className="text-xs text-[#7B1113] font-medium hover:underline mt-1 w-fit"
              >
                {t('common.verify')} →
              </Link>
            </div>
            <div className="flex flex-col gap-2 items-start md:items-end">
              <span className="text-xs text-[#8A8A8A] font-light">{t('whereToBuy.reportCounterfeit')}</span>
              <Link
                to={prefix + '/contact'}
                className="text-xs text-[#7B1113] font-medium hover:underline w-fit"
              >
                {t('whereToBuy.reportLink')}
              </Link>
            </div>
          </div>
        </div>
      </main>
      <SharedFooter />
    </>
  );
}
